import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  LinkIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

const PaymentManagement = () => {
  const { user, isCoach } = useAuth();
  const [currentTeam, setCurrentTeam] = useState(null);
  
  // Payment processor status
  const [processorStatus, setProcessorStatus] = useState({
    paypal: { connected: false, account_id: null, last_sync: null }
  });
  
  // Fee requests and assignments
  const [feeRequests, setFeeRequests] = useState([]);
  const [feeAssignments, setFeeAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // UI state
  const [showCreateFee, setShowCreateFee] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  
  // Form data
  const [feeFormData, setFeeFormData] = useState({
    title: '',
    description: '',
    amount: '',
    due_date: '',
    payment_processor: 'paypal',
    player_ids: []
  });

  // Fetch data on component mount
  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Get user's primary team
      const teamsResponse = await fetch('/api/teams/my-teams', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (teamsResponse.ok) {
        const teams = await teamsResponse.json();
        const primaryTeam = teams.find(team => team.is_primary) || teams[0];
        setCurrentTeam(primaryTeam);
        
        if (primaryTeam) {
          await fetchProcessorStatus(primaryTeam.id);
          await fetchFeeRequests(primaryTeam.id);
          await fetchFeeAssignments();
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProcessorStatus = async (teamId) => {
    try {
      const response = await fetch(`/api/payment-processors/team/${teamId}/status`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const status = await response.json();
        setProcessorStatus(status);
      }
    } catch (error) {
      console.error('Error fetching processor status:', error);
    }
  };

  const fetchFeeRequests = async (teamId) => {
    try {
      const response = await fetch(`/api/fees/team/${teamId}/fee-requests`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFeeRequests(data.feeRequests || []);
      }
    } catch (error) {
      console.error('Error fetching fee requests:', error);
    }
  };

  const fetchFeeAssignments = async () => {
    try {
      const response = await fetch(`/api/fees/user/${user.id}/fee-assignments`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const assignments = await response.json();
        setFeeAssignments(assignments);
      }
    } catch (error) {
      console.error('Error fetching fee assignments:', error);
    }
  };

  const connectPayPal = async () => {
    try {
      const response = await fetch(`/api/payment-processors/paypal/oauth/authorize/${currentTeam.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.authUrl;
      }
    } catch (error) {
      console.error('Error initiating PayPal OAuth:', error);
    }
  };


  const createFeeRequest = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`/api/fees/team/${currentTeam.id}/fee-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...feeFormData,
          player_ids: selectedPlayers
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setFeeRequests(prev => [data.feeRequest, ...prev]);
        setShowCreateFee(false);
        setFeeFormData({
          title: '',
          description: '',
          amount: '',
          due_date: '',
          payment_processor: 'paypal',
          player_ids: []
        });
        setSelectedPlayers([]);
      }
    } catch (error) {
      console.error('Error creating fee request:', error);
    }
  };

  const sendFeeRequest = async (feeRequestId) => {
    try {
      const response = await fetch(`/api/fees/fee-requests/${feeRequestId}/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        await fetchFeeRequests(currentTeam.id);
        toast.success('Fee request sent successfully!');
      }
    } catch (error) {
      console.error('Error sending fee request:', error);
    }
  };

  const handlePlayerSelection = (playerId) => {
    setSelectedPlayers(prev => 
      prev.includes(playerId) 
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId]
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircleIcon className="h-4 w-4" />;
      case 'sent': return <LinkIcon className="h-4 w-4" />;
      case 'pending': return <ClockIcon className="h-4 w-4" />;
      case 'overdue': return <ExclamationTriangleIcon className="h-4 w-4" />;
      default: return <ClockIcon className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment management...</p>
        </div>
      </div>
    );
  }

  if (!currentTeam) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">No Team Found</h2>
        <p className="text-gray-600">Please join a team to access payment management features.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Payment Management</h1>
            <p className="text-gray-600 mt-1">
              {isCoach 
                ? `Manage payments for ${currentTeam.name}`
                : 'View your payment obligations and history'
              }
            </p>
          </div>
          {isCoach && (
            <button
              onClick={() => setShowCreateFee(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create Fee Request
            </button>
          )}
        </div>
      </div>

      {/* Payment Processor Status */}
      {isCoach && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Payment Processors</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* PayPal Status */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center mr-3">
                    <CreditCardIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">PayPal</h3>
                    <p className="text-xs text-gray-500">
                      {processorStatus.paypal.connected ? 'Connected' : 'Not Connected'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  {processorStatus.paypal.connected ? (
                    <CheckIcon className="h-5 w-5 text-green-500" />
                  ) : (
                    <button
                      onClick={connectPayPal}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Connect
                    </button>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Fee Requests */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Fee Requests</h2>
          <p className="text-sm text-gray-600 mt-1">
            {isCoach ? 'Manage fee requests for your team' : 'Your assigned fees and payments'}
          </p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Processor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {feeRequests.map((feeRequest) => (
                <tr key={feeRequest.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{feeRequest.title}</div>
                      <div className="text-sm text-gray-500">{feeRequest.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatCurrency(feeRequest.amount)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {feeRequest.due_date ? formatDate(feeRequest.due_date) : 'No due date'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {feeRequest.payment_processor}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(feeRequest.status)}`}>
                      {getStatusIcon(feeRequest.status)}
                      <span className="ml-1 capitalize">{feeRequest.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowPaymentDetails(feeRequest)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      {isCoach && feeRequest.status === 'draft' && (
                        <button
                          onClick={() => sendFeeRequest(feeRequest.id)}
                          className="text-green-600 hover:text-green-900"
                        >
                          Send
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Fee Request Modal */}
      {showCreateFee && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create Fee Request</h3>
                <button
                  onClick={() => setShowCreateFee(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={createFeeRequest} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Title</label>
                    <input
                      type="text"
                      value={feeFormData.title}
                      onChange={(e) => setFeeFormData(prev => ({ ...prev, title: e.target.value }))}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={feeFormData.amount}
                      onChange={(e) => setFeeFormData(prev => ({ ...prev, amount: e.target.value }))}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={feeFormData.description}
                    onChange={(e) => setFeeFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows="3"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Due Date</label>
                    <input
                      type="date"
                      value={feeFormData.due_date}
                      onChange={(e) => setFeeFormData(prev => ({ ...prev, due_date: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Payment Processor</label>
                    <select
                      value={feeFormData.payment_processor}
                      onChange={(e) => setFeeFormData(prev => ({ ...prev, payment_processor: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      <option value="paypal">PayPal</option>
                    </select>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateFee(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Create Fee Request
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Payment Details Modal */}
      {showPaymentDetails && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Fee Request Details</h3>
                <button
                  onClick={() => setShowPaymentDetails(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Title</label>
                    <p className="mt-1 text-sm text-gray-900">{showPaymentDetails.title}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Amount</label>
                    <p className="mt-1 text-sm text-gray-900">{formatCurrency(showPaymentDetails.amount)}</p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <p className="mt-1 text-sm text-gray-900">{showPaymentDetails.description}</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Due Date</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {showPaymentDetails.due_date ? formatDate(showPaymentDetails.due_date) : 'No due date'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(showPaymentDetails.status)}`}>
                      {getStatusIcon(showPaymentDetails.status)}
                      <span className="ml-1 capitalize">{showPaymentDetails.status}</span>
                    </span>
                  </div>
                </div>
                
                {showPaymentDetails.FeeAssignments && showPaymentDetails.FeeAssignments.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assignments</label>
                    <div className="space-y-2">
                      {showPaymentDetails.FeeAssignments.map((assignment) => (
                        <div key={assignment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {assignment.Player?.firstName} {assignment.Player?.lastName}
                            </p>
                            <p className="text-xs text-gray-500">{assignment.Player?.email}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">
                              {formatCurrency(assignment.amount_owed)}
                            </p>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(assignment.status)}`}>
                              {assignment.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentManagement;
